"use client"

import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  MenuItem,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material"
import PaymentRecoveryService from "../../services/payment-recovery.service"
import useOrganizationParams from "../../hooks/useOrganizationParams"

const PaymentRecoverySettings = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState({
    maxRetryAttempts: 3,
    retryInterval: 3,
    retryIntervalUnit: "days",
    enableDunning: true,
    dunningEmails: [
      { daysBefore: 3, templateId: "payment-retry-reminder" },
      { daysBefore: 1, templateId: "payment-retry-urgent" },
      { daysBefore: 0, templateId: "payment-retry-final" },
    ],
    gracePeriod: 7,
    gracePeriodUnit: "days",
    enableAutoCancel: true,
    cancelAfter: 14,
    cancelAfterUnit: "days",
  })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  const organizationParams = useOrganizationParams()

  useEffect(() => {
    fetchSettings()
  }, [organizationParams])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const response = await PaymentRecoveryService.getSettings(organizationParams)

      // Check if response is valid and has data
      if (response && typeof response === "object") {
        // Set default values for any missing properties
        const defaultSettings = {
          maxRetryAttempts: 3,
          retryInterval: 3,
          retryIntervalUnit: "days",
          enableDunning: true,
          dunningEmails: [
            { daysBefore: 3, templateId: "payment-retry-reminder" },
            { daysBefore: 1, templateId: "payment-retry-urgent" },
            { daysBefore: 0, templateId: "payment-retry-final" },
          ],
          gracePeriod: 7,
          gracePeriodUnit: "days",
          enableAutoCancel: true,
          cancelAfter: 14,
          cancelAfterUnit: "days",
        }

        // Merge response with default settings
        setSettings({
          ...defaultSettings,
          ...response,
        })
      } else {
        // If response is invalid, use default settings
        console.error("Invalid settings response:", response)
        setSnackbar({
          open: true,
          message: "Failed to load settings. Using default values.",
          severity: "warning",
        })
      }
    } catch (error) {
      console.error("Error fetching payment recovery settings:", error)
      setSnackbar({
        open: true,
        message: "Failed to load settings. Using default values.",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, checked } = e.target
    const newValue = e.target.type === "checkbox" ? checked : value

    setSettings({
      ...settings,
      [name]: newValue,
    })
  }

  const handleTimeUnitChange = (e, field) => {
    setSettings({
      ...settings,
      [field]: e.target.value,
    })
  }

  const handleDunningEmailChange = (index, field, value) => {
    const updatedEmails = [...settings.dunningEmails]
    updatedEmails[index] = {
      ...updatedEmails[index],
      [field]: value,
    }

    setSettings({
      ...settings,
      dunningEmails: updatedEmails,
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await PaymentRecoveryService.updateSettings(settings, organizationParams)
      setSnackbar({
        open: true,
        message: "Settings saved successfully",
        severity: "success",
      })
    } catch (error) {
      console.error("Error saving settings:", error)
      setSnackbar({
        open: true,
        message: "Failed to save settings",
        severity: "error",
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    })
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Payment Recovery Settings
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Configure how TinyTrack handles failed payments and recovery attempts.
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Retry Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Maximum Retry Attempts"
              name="maxRetryAttempts"
              type="number"
              value={settings.maxRetryAttempts}
              onChange={handleChange}
              inputProps={{ min: 1, max: 10 }}
              helperText="Maximum number of times to retry a failed payment"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Retry Interval"
              name="retryInterval"
              type="number"
              value={settings.retryInterval}
              onChange={handleChange}
              inputProps={{ min: 1 }}
              helperText="Time between retry attempts"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Interval Unit"
              name="retryIntervalUnit"
              value={settings.retryIntervalUnit}
              onChange={(e) => handleTimeUnitChange(e, "retryIntervalUnit")}
              helperText="Unit of time for retry interval"
            >
              <MenuItem value="hours">Hours</MenuItem>
              <MenuItem value="days">Days</MenuItem>
              <MenuItem value="weeks">Weeks</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" gutterBottom>
          Dunning Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={settings.enableDunning} onChange={handleChange} name="enableDunning" color="primary" />
              }
              label="Enable Dunning Emails"
            />
            <Typography variant="body2" color="textSecondary">
              Send reminder emails to customers before and after payment retry attempts
            </Typography>
          </Grid>

          {settings.enableDunning && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Dunning Email Schedule
                </Typography>
              </Grid>

              {settings.dunningEmails.map((email, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={`Email ${index + 1} - Days Before`}
                      type="number"
                      value={email.daysBefore}
                      onChange={(e) => handleDunningEmailChange(index, "daysBefore", Number.parseInt(e.target.value))}
                      inputProps={{ min: 0 }}
                      helperText="Days before payment retry to send this email"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={`Email ${index + 1} - Template ID`}
                      value={email.templateId}
                      onChange={(e) => handleDunningEmailChange(index, "templateId", e.target.value)}
                      helperText="Email template identifier"
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </>
          )}
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" gutterBottom>
          Grace Period Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Grace Period"
              name="gracePeriod"
              type="number"
              value={settings.gracePeriod}
              onChange={handleChange}
              inputProps={{ min: 0 }}
              helperText="Time to keep subscription active after payment failure"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Grace Period Unit"
              name="gracePeriodUnit"
              value={settings.gracePeriodUnit}
              onChange={(e) => handleTimeUnitChange(e, "gracePeriodUnit")}
              helperText="Unit of time for grace period"
            >
              <MenuItem value="hours">Hours</MenuItem>
              <MenuItem value="days">Days</MenuItem>
              <MenuItem value="weeks">Weeks</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" gutterBottom>
          Cancellation Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enableAutoCancel}
                  onChange={handleChange}
                  name="enableAutoCancel"
                  color="primary"
                />
              }
              label="Enable Automatic Cancellation"
            />
            <Typography variant="body2" color="textSecondary">
              Automatically cancel subscriptions after all recovery attempts fail
            </Typography>
          </Grid>

          {settings.enableAutoCancel && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cancel After"
                  name="cancelAfter"
                  type="number"
                  value={settings.cancelAfter}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                  helperText="Time after last retry attempt to cancel subscription"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Cancel After Unit"
                  name="cancelAfterUnit"
                  value={settings.cancelAfterUnit}
                  onChange={(e) => handleTimeUnitChange(e, "cancelAfterUnit")}
                  helperText="Unit of time for cancellation period"
                >
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
        </Grid>

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving && <CircularProgress size={20} color="inherit" />}
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default PaymentRecoverySettings

